import React from "react";
import { Drawer } from "antd";
import SendDirectMessage from "../../engagements/activityCenter/PinMessages/SendDirectMessage";
import ViewMessageNotes from "../../engagements/activityCenter/PinMessages/ViewMessageNotes";
import PhoneBotForm from "../../adminDashboard/phoneBots/phoneBotsForm";
import { OttoSettingsForm } from "../../userSettings/ottoSettingsForm";
import PhoneDetailsDrawerContent from "../../adminDashboard/adminOptions/goToIntegration/PhoneDetailsDrawerContent";
import AssignUserDrawer from "../../adminDashboard/adminOptions/goToIntegration/AssignUserDrawer";
import AssignExtensionDrawer from "../../adminDashboard/adminOptions/goToIntegration/AssignExtensionDrawer";
import AssignDepartmentDrawer from "../../adminDashboard/adminOptions/goToIntegration/AssignDepartmentDrawer";
import UserDetailsDrawerContent from "../../adminDashboard/adminOptions/goToIntegration/UserDetailsDrawerContent";
import ExtensionDetailsDrawerContent from "../../adminDashboard/adminOptions/goToIntegration/ExtensionDetailsDrawerContent";
import DeviceDetailsDrawerContent from "../../adminDashboard/adminOptions/goToIntegration/DeviceDetailsDrawerContent";
import ReviewPlatformDrawerForm from "../../adminDashboard/adminOptions/reviewSettings/ReviewPlatformDrawerForm";
import VehicleTradeDrawer from "../../ottoAnalytics/VehicleTradeDrawer";
import AssignLocationDrawer from "../../adminDashboard/adminOptions/goToIntegration/AssignLocationDrawer";
import OpportunitySalesDrawerContent from "../../engagements/dashboard/OpportunitySalesDrawer";

/**
 * DRAWER HOC USAGE EXAMPLES
 *
 * How to use openDrawer function:
 *
 * import { openDrawer, closeDrawer, updateDrawer } from "../../../library/helpers";
 *
 * // Basic usage
 * openDrawer(
 *   "drawerName",           // Drawer type/name (must match case in switch)
 *   {                       // Drawer Props (Ant Design Drawer properties)
 *     title: "My Drawer",
 *     width: 500,
 *     placement: "right",
 *     maskClosable: false,
 *     destroyOnClose: true
 *   },
 *   {                       // Component Props (data for your component inside drawer)
 *     userId: 123,
 *     mode: "edit",
 *     data: { name: "John" },
 *     onSave: (data) => console.log("Saved:", data),
 *     onCancel: () => console.log("Cancelled")
 *   }
 * );
 *
 * // Multiple drawers (stack behavior)
 * openDrawer("userList", { width: 600 }, { filters: {...} });
 * openDrawer("userEdit", { width: 400 }, { userId: 123 });
 * openDrawer("confirmDialog", { width: 300 }, { message: "Are you sure?" });
 *
 * // Closing drawers
 * closeDrawer();           // Closes topmost drawer, previous becomes visible
 *
 * // Updating drawer content (NEW FEATURE!)
 * // This allows buttons in drawer title/footer to trigger re-renders of inner component
 * updateDrawer({           // Updates component props of current drawer
 *   showAddForm: true,
 *   selectedTab: "notes",
 *   refreshData: Date.now()
 * });
 *
 * // Available Drawer Props (drawerProps):
 * {
 *   title: string,
 *   width: number,
 *   height: number,
 *   placement: "left" | "right" | "top" | "bottom",
 *   mask: boolean,
 *   maskClosable: boolean,
 *   closable: boolean,
 *   destroyOnClose: boolean,
 *   getContainer: HTMLElement | (() => HTMLElement) | string | false,
 *   style: CSSProperties,
 *   className: string,
 *   zIndex: number,
 *   extra: ReactNode,
 *   footer: ReactNode,
 *   footerStyle: CSSProperties,
 *   headerStyle: CSSProperties,
 *   bodyStyle: CSSProperties,
 *   push: boolean,
 *   forceRender: boolean,
 *   loading: boolean,
 *   size: "default" | "large"
 * }
 *
 * // Component Props (componentProps) - your custom data:
 * {
 *   // Any props your component needs
 *   userId: number,
 *   data: object,
 *   onSave: function,
 *   onCancel: function,
 *   handleCancelBtn: function,    // Custom cancel handler
 *   handleDrawerClose: function,  // Called when drawer closes
 *   updateDrawer: function,       // Function to update drawer props (automatically injected)
 *   // ... any other props
 * }
 *
 * // Inside your component, you can use updateDrawer:
 * const MyComponent = ({ userId, updateDrawer }) => {
 *   const handleButtonClick = () => {
 *     updateDrawer({ showForm: true, activeTab: 'details' });
 *   };
 *
 *   return <button onClick={handleButtonClick}>Update Drawer</button>;
 * };
 */

export const DRAWER_TYPES = {
  // Define your drawer types here
  SEND_DIRECT_MESSAGE: "SendDirectMessage",
  VIEW_MESSAGE_NOTES: "ViewMessageNotes",
  PHONE_BOT: "PhoneBot",
  AI_PROMPT: "AiPrompt",
  GOTO_PHONE_DETAILS: "PhoneDetails",
  GOTO_USER_DETAILS: "UserDetails",
  GOTO_EXTENSION_DETAILS: "ExtensionDetails",
  GOTO_DEVICE_DETAILS: "DeviceDetails",
  GOTO_ASSIGN_USER: "AssignUser",
  GOTO_ASSIGN_EXTENSION: "AssignExtension",
  GOTO_ASSIGN_DEPARTMENT: "AssignDepartment",
  GOTO_ASSIGN_LOCATION: "AssignLocation",
  REVIEW_PLATFORM: "ReviewPlatform",
  VEHICLE_TRADE_LIST: "VehicleTradeList",
  OPPORTUNITY_SALES_DETAIL: "OpportunitySalesDetail",
};

const DrawerComponent = React.memo(props => {
  const { drawerProps, componentProps, type, handleClose, open, updateDrawerHandler } = props;

  const drawerContent = () => {
    // Add updateDrawer function to componentProps so inner components can use it
    const enhancedProps = {
      ...componentProps,
      updateDrawer: updateDrawerHandler,
    };

    switch (type) {
      case DRAWER_TYPES.SEND_DIRECT_MESSAGE:
        return <SendDirectMessage {...enhancedProps} />;
      case DRAWER_TYPES.VIEW_MESSAGE_NOTES:
        return <ViewMessageNotes {...enhancedProps} />;
      case DRAWER_TYPES.PHONE_BOT:
        return <PhoneBotForm {...enhancedProps} />;
      case DRAWER_TYPES.AI_PROMPT:
        return <OttoSettingsForm {...enhancedProps} />;
      case DRAWER_TYPES.GOTO_PHONE_DETAILS:
        return <PhoneDetailsDrawerContent {...enhancedProps} />;
      case DRAWER_TYPES.GOTO_USER_DETAILS:
        return <UserDetailsDrawerContent {...enhancedProps} />;
      case DRAWER_TYPES.GOTO_EXTENSION_DETAILS:
        return <ExtensionDetailsDrawerContent {...enhancedProps} />;
      case DRAWER_TYPES.GOTO_DEVICE_DETAILS:
        return <DeviceDetailsDrawerContent {...enhancedProps} />;
      case DRAWER_TYPES.GOTO_ASSIGN_USER:
        return <AssignUserDrawer {...enhancedProps} />;
      case DRAWER_TYPES.GOTO_ASSIGN_EXTENSION:
        return <AssignExtensionDrawer {...enhancedProps} />;
      case DRAWER_TYPES.GOTO_ASSIGN_DEPARTMENT:
        return <AssignDepartmentDrawer {...enhancedProps} />;
      case DRAWER_TYPES.GOTO_ASSIGN_LOCATION:
        return <AssignLocationDrawer {...enhancedProps} />;
      case DRAWER_TYPES.REVIEW_PLATFORM:
        return <ReviewPlatformDrawerForm {...enhancedProps} />;
      case DRAWER_TYPES.VEHICLE_TRADE_LIST:
        return <VehicleTradeDrawer {...enhancedProps} />;
      case DRAWER_TYPES.OPPORTUNITY_SALES_DETAIL:
        return <OpportunitySalesDrawerContent {...enhancedProps} />;
      default:
        return <div>No content available for drawer type: {type}</div>;
    }
  };

  const closeDrawerFunction = () => {
    if (componentProps?.handleCancelBtn) {
      componentProps.handleCancelBtn();
    } else {
      handleClose();
    }

    componentProps?.handleDrawerClose && componentProps?.handleDrawerClose();
  };

  return open ? (
    <Drawer
      title={drawerProps?.title || "Drawer"}
      open={open}
      onClose={closeDrawerFunction}
      closable
      width={drawerProps?.width}
      height={drawerProps?.height}
      placement={drawerProps?.placement || "right"}
      size={drawerProps?.size || "default"}
      mask={drawerProps?.mask !== false}
      maskClosable={drawerProps?.maskClosable !== false}
      destroyOnClose={drawerProps?.destroyOnClose !== undefined ? drawerProps.destroyOnClose : true}
      getContainer={drawerProps?.getContainer}
      style={drawerProps?.style}
      className={drawerProps?.className}
      zIndex={drawerProps?.zIndex}
      extra={drawerProps?.extra}
      footer={drawerProps?.footer}
      footerStyle={drawerProps?.footerStyle}
      headerStyle={drawerProps?.headerStyle}
      bodyStyle={drawerProps?.bodyStyle}
      push={drawerProps?.push}
      forceRender={drawerProps?.forceRender}
      loading={drawerProps?.loading}
    >
      {open && <div className="w-100">{drawerContent()}</div>}
    </Drawer>
  ) : null;
});

DrawerComponent.displayName = "DrawerComponent";

export default DrawerComponent;
